'use client';

import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Title } from '@/shared/components';
import { Button } from '@/shared/components/primitives/button';
import { MessageBox } from '@/shared/components/message-box';
import { TextNavLink } from '@/shared/components/text-nav-link';
import { Mail, XCircle } from 'lucide-react';
import { pagesRoutes } from '@/shared/constants/routes';
import { type ResendStatus } from '@/features/auth/shared/api/resend-verification-request';
import { useResendVerification } from '@/features/auth/shared/hooks/use-resend-verification';

type VerificationError = 'expired' | 'invalid' | null;

type VerifyEmailContentProps = {
  initialEmail: string | null;
  verificationError: VerificationError;
  initialResendStatus: ResendStatus;
  extraContent?: ReactNode;
  returnTo?: string;
};

export const VerifyEmailContent = ({
  initialEmail,
  verificationError,
  initialResendStatus,
  extraContent = null,
  returnTo,
}: VerifyEmailContentProps) => {
  const t = useTranslations('Auth.verifyEmail');
  const email = initialEmail;
  const { resendStatus, handleResend } = useResendVerification({
    email,
    initialStatus: initialResendStatus,
    returnTo,
  });
  const isResendDisabled =
    resendStatus === 'sending' ||
    resendStatus === 'sent' ||
    resendStatus === 'cooldown' ||
    resendStatus === 'limitReached';
  const isVerificationError = verificationError === 'expired' || verificationError === 'invalid';

  return (
    <div className='container mx-auto flex max-w-xl flex-col items-center py-16 text-center'>
      {isVerificationError ? (
        <XCircle className='text-error mb-6 h-16 w-16' aria-hidden='true' />
      ) : (
        <Mail className='text-primary mb-6 h-16 w-16' aria-hidden='true' />
      )}
      <Title title={isVerificationError ? t(`errors.${verificationError}.title`) : t('title')} />
      {isVerificationError && (
        <div className='mt-4 w-full' role='alert'>
          <MessageBox type='error' closable={false}>
            {t(`errors.${verificationError}.description`)}
          </MessageBox>
        </div>
      )}
      {!isVerificationError && (
        <>
          <p className='text-text-muted mt-4'>{t('sentTo')}</p>
          {email && <p className='font-body-bold mt-2 text-lg'>{email}</p>}
          <p className='text-text-muted mt-4'>{t('checkInbox')}</p>
          <p className='text-text-muted mt-2 text-sm'>{t('checkSpam')}</p>
        </>
      )}
      {isVerificationError && email && <p className='font-body-bold mt-4 text-lg'>{email}</p>}
      {resendStatus === 'sent' && (
        <div className='mt-4 w-full'>
          <MessageBox type='success' closable={false}>
            {t('resendStatus.sent')}
          </MessageBox>
        </div>
      )}
      {resendStatus === 'cooldown' && (
        <div className='mt-4 w-full'>
          <MessageBox type='warning' closable={false}>
            {t('resendStatus.cooldown')}
          </MessageBox>
        </div>
      )}
      {resendStatus === 'limitReached' && (
        <div className='mt-4 w-full' role='alert'>
          <MessageBox type='error' closable={false}>
            {t('resendStatus.limitReached')}
          </MessageBox>
        </div>
      )}
      {resendStatus === 'error' && (
        <div className='mt-4 w-full' role='alert'>
          <MessageBox type='error' closable={false}>
            {t('resendStatus.error')}
          </MessageBox>
        </div>
      )}
      <Button
        type='button'
        variant='primary'
        size='md'
        className='mt-8'
        onClick={handleResend}
        disabled={isResendDisabled}
      >
        {resendStatus === 'sending' ? t('resendSending') : t('resendButton')}
      </Button>
      {extraContent && <div className='mt-8 w-full'>{extraContent}</div>}
      <p className='text-text-muted mt-6 text-sm'>
        {t('havingTrouble')}{' '}
        <TextNavLink
          href={pagesRoutes.support.href}
          isUnderline
          unlocalized
          target='_blank'
          rel='noopener noreferrer'
        >
          {t('contactSupport')}
        </TextNavLink>
      </p>
    </div>
  );
};
